import { MAX_ACCOUNT_SLOTS } from '@aiai/shared';

/**
 * Which signed-in account this TAB is (Gmail-style `/u/N`). Decided once from
 * the URL at boot and fixed for the tab's lifetime: switching account is a
 * full navigation, so live sockets/queries never change identity underneath.
 * Framework-free like `token.ts`, so the axios layer can read it.
 */
const SLOT_PREFIX = new RegExp(`^/u/([0-${MAX_ACCOUNT_SLOTS - 1}])(?=/|$)`);

export function parseSlotFromPath(pathname: string): number {
  const m = SLOT_PREFIX.exec(pathname);
  return m ? Number(m[1]) : 0;
}

export function stripSlot(pathname: string): string {
  return pathname.replace(SLOT_PREFIX, '') || '/';
}

/** Absolute browser URL for `path` (which may carry ?query/#hash) under `slot`. */
export function slotPath(slot: number, path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  return slot === 0 ? p : `/u/${slot}${p}`;
}

export function routerBasename(slot: number): string | undefined {
  return slot === 0 ? undefined : `/u/${slot}`;
}

let currentSlot = typeof window === 'undefined' ? 0 : parseSlotFromPath(window.location.pathname);

export function getSlot(): number {
  return currentSlot;
}

export function setSlotForTesting(slot: number): void {
  currentSlot = slot;
}

/** Per-account localStorage key; slot 0 keeps the pre-multi-account key. */
export function slotStorageKey(base: string, slot = currentSlot): string {
  return slot === 0 ? base : `${base}.${slot}`;
}

/** The current tab's in-app path (slot prefix removed) incl. query + hash. */
export function currentAppPath(): string {
  const { pathname, search, hash } = window.location;
  return `${stripSlot(pathname)}${search}${hash}`;
}

export function lowestFreeSlot(accounts: { slot: number }[]): number | null {
  for (let slot = 0; slot < MAX_ACCOUNT_SLOTS; slot++) {
    if (!accounts.some((a) => a.slot === slot)) return slot;
  }
  return null;
}

// The user this tab's slot belonged to when the tab adopted it. If a later
// refresh of the same slot returns someone else (the slot was signed out and
// re-used in another tab), this tab must not carry on as that person.
let slotOwner: string | null = null;
// Sticky once an owner mismatch was detected: the page is navigating away, so
// nothing in this document may adopt the new user in the meantime.
let ownerChanged = false;

export function adoptSlotOwner(userId: string): boolean {
  if (ownerChanged) return false;
  if (slotOwner === null) {
    slotOwner = userId;
    return true;
  }
  return slotOwner === userId;
}

export function resetOwnerChangedForTesting(): void {
  ownerChanged = false;
}

export function setSlotOwner(userId: string | null): void {
  slotOwner = userId;
}

export function handleOwnerChanged(): void {
  slotOwner = null;
  ownerChanged = true;
  window.location.assign('/accounts');
}

/**
 * The server may write a sign-in to a different slot than this tab's (that
 * user is already signed in there, or this slot belongs to someone else).
 * Follow it with a full navigation. Returns true when navigating away.
 */
export function followSessionSlot(res: { slot?: number }, to: string): boolean {
  if (res.slot === undefined || res.slot === currentSlot) return false;
  window.location.assign(slotPath(res.slot, to));
  return true;
}
